import { ensureUrlWithProtocol, isProbablyUrl } from '../validation/url.util';

export interface LinkifiedTextPart {
  type: 'text' | 'url';
  value: string;
  href?: string;
}

const NON_WHITESPACE_PATTERN = /\S+/g;
const LEADING_PUNCTUATION_PATTERN = /^[([{'"<]+/;

const ALWAYS_TRAILING_PUNCTUATION = new Set(['.', ',', '!', '?', ';', ':']);
const WRAPPING_PUNCTUATION_PAIRS: Record<string, string> = {
  ')': '(',
  ']': '[',
  '}': '{',
  '>': '<',
};
const QUOTE_PUNCTUATION = new Set(["'", '"']);

interface ParsedWordResult {
  leadingText: string;
  url: string | null;
  trailingText: string;
}

function shouldAutoLinkCandidate(candidate: string): boolean {
  if (!isProbablyUrl(candidate)) {
    return false;
  }

  if (/^https?:\/\//i.test(candidate) || /^www\./i.test(candidate)) {
    return true;
  }

  return /[/?#]/.test(candidate);
}

function countOccurrences(value: string, target: string): number {
  return [...value].filter((character) => character === target).length;
}

function shouldTrimTrailingCharacter(
  candidate: string,
  character: string
): boolean {
  if (ALWAYS_TRAILING_PUNCTUATION.has(character)) {
    return true;
  }

  const matchingOpeningCharacter = WRAPPING_PUNCTUATION_PAIRS[character];

  if (matchingOpeningCharacter) {
    return (
      countOccurrences(candidate, character) >
      countOccurrences(candidate, matchingOpeningCharacter)
    );
  }

  if (QUOTE_PUNCTUATION.has(character)) {
    return countOccurrences(candidate, character) % 2 === 1;
  }

  return false;
}

function splitTrailingPunctuation(word: string): {
  candidate: string;
  trailingText: string;
} {
  let candidate = word;
  let trailingText = '';

  while (candidate.length > 0) {
    const trailingCharacter = candidate.at(-1);

    if (
      !trailingCharacter ||
      !shouldTrimTrailingCharacter(candidate, trailingCharacter)
    ) {
      break;
    }

    trailingText = `${trailingCharacter}${trailingText}`;
    candidate = candidate.slice(0, -1);
  }

  return {
    candidate,
    trailingText,
  };
}

function parseWord(word: string): ParsedWordResult {
  const leadingText = LEADING_PUNCTUATION_PATTERN.exec(word)?.[0] ?? '';
  const { candidate, trailingText } = splitTrailingPunctuation(
    word.slice(leadingText.length)
  );

  if (!shouldAutoLinkCandidate(candidate)) {
    return {
      leadingText: '',
      url: null,
      trailingText: '',
    };
  }

  return {
    leadingText,
    url: candidate,
    trailingText,
  };
}

function mergeAdjacentTextParts(
  parts: LinkifiedTextPart[]
): LinkifiedTextPart[] {
  return parts.reduce<LinkifiedTextPart[]>((accumulator, part) => {
    const previousPart = accumulator.at(-1);

    if (part.type === 'text' && previousPart?.type === 'text') {
      previousPart.value += part.value;
      return accumulator;
    }

    accumulator.push(part);
    return accumulator;
  }, []);
}

/**
 * Splits a plain text string into text and URL parts while preserving spacing.
 */
export function linkifyTextParts(text: string): LinkifiedTextPart[] {
  if (!text) return [];

  const parts: LinkifiedTextPart[] = [];
  let cursor = 0;

  for (const match of text.matchAll(NON_WHITESPACE_PATTERN)) {
    const value = match[0];
    const index = match.index;

    if (index > cursor) {
      parts.push({
        type: 'text',
        value: text.slice(cursor, index),
      });
    }

    const { leadingText, url, trailingText } = parseWord(value);

    if (!url) {
      parts.push({ type: 'text', value });
      cursor = index + value.length;
      continue;
    }

    if (leadingText) {
      parts.push({ type: 'text', value: leadingText });
    }

    parts.push({
      type: 'url',
      value: url,
      href: ensureUrlWithProtocol(url),
    });

    if (trailingText) {
      parts.push({ type: 'text', value: trailingText });
    }

    cursor = index + value.length;
  }

  if (cursor < text.length) {
    parts.push({
      type: 'text',
      value: text.slice(cursor),
    });
  }

  return mergeAdjacentTextParts(parts);
}
